import { useState, useEffect, useRef } from 'react';
import { getBillingPackages } from '../api';
import type { BillingPackage } from '../types';
import './CreateInstanceModal.css';
import './PackageSelectModal.css';

interface PackageSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCheckout: (packageName: string, packageId: string, instanceName: string) => void;
  isProcessing: boolean;
}

function getFeatureValue(features: Record<string, unknown>, key: string): string | null {
  const value = features[key];
  if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  if (typeof value === 'number') return String(value);
  return null;
}

function getCompactSpec(cpuRaw: string | null, ramRaw: string | null, tier: string): string {
  if (!cpuRaw || !ramRaw) return `Tier ${tier}`;

  const cpuMatch = cpuRaw.match(/(\d+(?:\.\d+)?)/);
  const ramMatch = ramRaw.match(/(\d+(?:\.\d+)?)\s*([GM]B)/i);
  if (!cpuMatch || !ramMatch) return `Tier ${tier}`;

  const cpu = cpuMatch[1];
  const ram = `${ramMatch[1]}${ramMatch[2].toUpperCase()}`;
  return `${cpu}C${ram}`;
}

export default function PackageSelectModal({
  isOpen,
  onClose,
  onCheckout,
  isProcessing,
}: PackageSelectModalProps) {
  const [packages, setPackages] = useState<BillingPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [instanceName, setInstanceName] = useState('');
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch packages when modal opens
  useEffect(() => {
    if (!isOpen) return;

    let isActive = true;
    setLoading(true);
    setError(null);
    setInstanceName('');
    setSelectedPackage(null);

    getBillingPackages()
      .then((pkgs) => {
        if (!isActive) return;
        setPackages(pkgs);
        setSelectedPackage(pkgs[0]?.id ?? null);
        setLoading(false);
      })
      .catch((err) => {
        if (!isActive) return;
        setPackages([]);
        setError(err instanceof Error ? err.message : 'Failed to load packages');
        setLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [isOpen]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen && !isProcessing) {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isProcessing, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPackage || isProcessing) return;

    const trimmedName = instanceName.trim() || 'My Agent';
    const pkg = packages.find((p) => p.id === selectedPackage);
    if (pkg) {
      onCheckout(pkg.name, pkg.id, trimmedName);
    }
  };

  if (!isOpen) return null;
  const hasPackages = packages.length > 0;

  return (
    <div className="modal-overlay" onClick={isProcessing ? undefined : onClose}>
      <div className="modal-content package-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-emoji">💳</div>
          <h3>Choose Your Plan</h3>
          <p className="modal-subtitle">Select a plan to create your AI agent</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Instance Name Input */}
            <div className="input-group">
              <label htmlFor="instance-name">Agent Name</label>
              <input
                ref={inputRef}
                id="instance-name"
                type="text"
                value={instanceName}
                onChange={(e) => setInstanceName(e.target.value)}
                placeholder="e.g. Assistant, Helper, Buddy..."
                maxLength={30}
                disabled={isProcessing}
              />
            </div>

            {/* Package Selection */}
            {loading ? (
              <div className="loading-packages">
                <span className="btn-spinner"></span>
                Loading plans...
              </div>
            ) : error ? (
              <div className="error-message">{error}</div>
            ) : !hasPackages ? (
              <div className="empty-packages">
                No billing plans available right now. Please try again later.
              </div>
            ) : (
              <div className="packages-grid">
                {packages.map((pkg) => {
                  const cpu = getFeatureValue(pkg.features, 'cpu');
                  const ram = getFeatureValue(pkg.features, 'ram');
                  const hardwareText = getCompactSpec(cpu, ram, pkg.machineTier);

                  return (
                    <button
                      key={pkg.id}
                      type="button"
                      className={`package-card ${selectedPackage === pkg.id ? 'selected' : ''}`}
                      onClick={() => !isProcessing && setSelectedPackage(pkg.id)}
                      aria-pressed={selectedPackage === pkg.id}
                      disabled={isProcessing}
                    >
                      <div className="package-name">{pkg.name}</div>
                      <div className="package-price">
                        <span className="price-amount">${pkg.priceUsd}</span>
                        <span className="price-period">/month</span>
                      </div>
                      <div className="package-features">
                        <div className="feature">
                          <span className="feature-icon">⏱️</span>
                          <span>{pkg.runtimeHours}h runtime</span>
                        </div>
                        <div className="feature">
                          <span className="feature-icon">🤖</span>
                          <span>{pkg.aiCreditsLimit.toLocaleString()} AI credits</span>
                        </div>
                        <div className="feature">
                          <span className="feature-icon">🚀</span>
                          <span>{hardwareText}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!selectedPackage || isProcessing || loading || !hasPackages}
            >
              {isProcessing ? (
                <>
                  <span className="btn-spinner"></span>
                  Processing...
                </>
              ) : (
                <>
                  <span>🚀</span>
                  Continue to Payment
                </>
              )}
            </button>
          </div>
        </form>

        {!isProcessing && (
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        )}
      </div>
    </div>
  );
}
